import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import useWallet from '../hooks/useWallet';
import { Address } from '../lib/address/Address';
import { getKey, useBalanceStore } from '../stores/balanceStore';
import { useManualDestAddressesStore } from '../stores/manualDestAddressesStore';
const SwapAccountsStateContext = createContext(null);
const SwapAccountsUpdateContext = createContext(null);
export function SwapAccountsProvider({ children }) {
    const [selectedDestAccounts, setSelectedDestinationAccounts] = useState([]);
    const [selectedSourceAccounts, setSelectedSourceAccounts] = useState([]);
    const [lastSelectedSource, setLastSelectedSource] = useState(undefined);
    const { providers } = useWallet();
    const addManualDestAddress = useManualDestAddressesStore(s => s.addManualDestAddress);
    const manualDestAddresses = useManualDestAddressesStore(s => s.manualDestAddresses);
    const sourceAccounts = useMemo(() => {
        return providers.map(provider => {
            if (!hasWallet(provider))
                return null;
            const selectedWallet = provider.connectedWallets?.find(wallet => wallet.id === selectedSourceAccounts.find(acc => acc.providerName === provider.name && wallet.addresses.some(a => Address.equals(a, acc.address, null, provider.name)))?.id && wallet.addresses);
            const wallet = selectedWallet || provider.activeWallet;
            const selectedAccountAddress = selectedWallet ? selectedSourceAccounts.find(acc => acc.providerName === provider.name && acc.id === selectedWallet.id)?.address : undefined;
            const address = selectedAccountAddress ? selectedAccountAddress : wallet.address;
            const res = ResolveWalletSwapAccount(provider, wallet, address);
            if (!selectedAccountAddress) {
                setSelectedSourceAccounts(prev => {
                    const existingAccountIndex = prev.findIndex(acc => acc.providerName === res.providerName);
                    if (existingAccountIndex !== -1) {
                        const updatedAccounts = [...prev];
                        updatedAccounts[existingAccountIndex] = res;
                        return updatedAccounts;
                    }
                    return [...prev, res];
                });
            }
            return res;
        }).filter(Boolean);
    }, [providers, selectedSourceAccounts]);
    const destinationAccounts = useMemo(() => {
        return providers.map(provider => {
            const manuallyAdded = selectedDestAccounts.find(acc => acc.providerName === provider.name && acc.id === 'manually_added');
            const manualStillExists = manuallyAdded && manualDestAddresses.some(m => m.providerName === provider.name && Address.equals(m.address, manuallyAdded.address, null, provider.name));
            if (manuallyAdded && manualStillExists) {
                return ResolveManualSwapAccount(provider, manuallyAdded.address);
            }
            if (!hasWallet(provider))
                return null;
            const selectedWallet = provider.connectedWallets?.find(wallet => wallet.id === selectedDestAccounts.find(acc => acc.providerName === provider.name && wallet.addresses.some(a => Address.equals(a, acc.address, null, provider.name)))?.id && wallet.addresses);
            const wallet = selectedWallet || provider.activeWallet;
            const selectedAccountAddress = selectedWallet ? selectedDestAccounts.find(acc => acc.providerName === provider.name && acc.id === selectedWallet.id)?.address : undefined;
            const address = selectedAccountAddress ? selectedAccountAddress : wallet.address;
            return ResolveWalletSwapAccount(provider, wallet, address);
        }).filter(Boolean);
    }, [providers, selectedDestAccounts, manualDestAddresses]);
    const selectDestinationAccount = useCallback((account) => {
        if (account.id === 'manually_added' && account.address && account.providerName) {
            addManualDestAddress({ address: account.address, providerName: account.providerName });
        }
        setSelectedDestinationAccounts(prev => {
            const existingAccountIndex = prev.findIndex(acc => acc.providerName === account.providerName);
            if (existingAccountIndex !== -1) {
                const updatedAccounts = [...prev];
                updatedAccounts[existingAccountIndex] = account;
                return updatedAccounts;
            }
            return [...prev, account];
        });
    }, []);
    const selectSourceAccount = useCallback((account) => {
        setLastSelectedSource(account);
        const previousSourceAccount = sourceAccounts.find(acc => acc.providerName === account.providerName);
        if (destinationAccounts.some(acc => acc.address === previousSourceAccount?.address && acc.providerName === previousSourceAccount?.providerName)) {
            selectDestinationAccount(account);
        }
        setSelectedSourceAccounts(prev => {
            const existingAccountIndex = prev.findIndex(acc => acc.providerName === account.providerName);
            if (existingAccountIndex !== -1) {
                const updatedAccounts = [...prev];
                updatedAccounts[existingAccountIndex] = account;
                return updatedAccounts;
            }
            return [...prev, account];
        });
    }, [destinationAccounts, sourceAccounts]);
    const stateValues = useMemo(() => ({
        sourceAccounts,
        destinationAccounts,
        lastSelectedSource,
    }), [sourceAccounts, destinationAccounts, lastSelectedSource]);
    const update = useMemo(() => ({
        selectDestinationAccount,
        selectSourceAccount,
    }), [sourceAccounts, destinationAccounts, selectSourceAccount, selectDestinationAccount]);
    return (_jsx(SwapAccountsStateContext.Provider, { value: stateValues, children: _jsx(SwapAccountsUpdateContext.Provider, { value: update, children: children }) }));
}
export function useSwapAccounts(direction) {
    const values = useContext(SwapAccountsStateContext);
    if (values === undefined) {
        throw new Error('useSwapAccounts must be used within a SwapAccountsProvider');
    }
    return direction === "from" ? values.sourceAccounts : values.destinationAccounts;
}
export function useSelectedAccount(direction, networkName) {
    const values = useContext(SwapAccountsStateContext);
    if (!networkName)
        return undefined;
    if (values === undefined) {
        throw new Error('useSwapAccounts must be used within a SwapAccountsProvider');
    }
    return direction === "from" ? values.sourceAccounts.find(acc => acc.provider.withdrawalSupportedNetworks?.some(n => n === networkName))
        :
            values.destinationAccounts.find(acc => {
                if ('walletAutofillSupportedNetworks' in acc) {
                    return acc.walletAutofillSupportedNetworks?.some(n => n === networkName);
                }
                return acc.provider?.autofillSupportedNetworks?.some(n => n === networkName);
            });
}
/**
 * The full source account most recently chosen via selectSourceAccount,
 * resolved against the live source accounts (so it carries the wallet's
 * supported-network info). Returns undefined before any source pick. Used by
 * single-wallet flows that need to scope to the latest connected wallet rather
 * than every connected provider.
 */
export function useLatestSourceAccount() {
    const values = useContext(SwapAccountsStateContext);
    if (values === undefined) {
        throw new Error('useLatestSourceAccount must be used within a SwapAccountsProvider');
    }
    const { lastSelectedSource, sourceAccounts } = values;
    if (!lastSelectedSource)
        return undefined;
    return sourceAccounts.find(acc => acc.providerName === lastSelectedSource.providerName && acc.id === lastSelectedSource.id);
}
export function useNetworkBalanceKey(direction, networkName) {
    const account = useSelectedAccount(direction, networkName);
    if (!account || !networkName)
        return undefined;
    return getKey(account.address, networkName);
}
export function useNetworkBalance(direction, networkName) {
    const balanceKey = useNetworkBalanceKey(direction, networkName);
    const balance = useBalanceStore((s) => (s.balances[balanceKey || "unknown"]));
    return balance;
}
export function useManualDestAddresses() {
    return useManualDestAddressesStore(s => s.manualDestAddresses);
}
export function useSelectSwapAccount(direction) {
    const values = useContext(SwapAccountsUpdateContext);
    if (values === undefined) {
        throw new Error('useSelectSwapAccount must be used within a SwapAccountsUpdateContext');
    }
    return direction === "from" ? values.selectSourceAccount : values.selectDestinationAccount;
}
function hasWallet(p) {
    return Boolean(p.activeWallet);
}
function ResolveWalletSwapAccount(provider, wallet, address) {
    return {
        address,
        provider,
        providerName: provider.name,
        id: wallet.id,
        walletWithdrawalSupportedNetworks: wallet.withdrawalSupportedNetworks,
        walletAutofillSupportedNetworks: wallet.autofillSupportedNetworks,
        walletAsSourceSupportedNetworks: wallet.asSourceSupportedNetworks,
        displayName: wallet.displayName || provider.name,
        addresses: wallet.addresses || [address],
        icon: wallet.icon,
    };
}
function ResolveManualSwapAccount(provider, address) {
    return {
        address,
        provider,
        providerName: provider.name,
        id: 'manually_added',
        displayName: "Manual",
        addresses: [address],
        // No icon: consumers fall back to a live <AddressIcon> when the URL is
        // absent. AddressIcon draws via useEffect, so it can't be statically
        // rendered into a data URL here.
    };
}
